export * from './user';

import axios from 'axios';
import { useAuthStore, useGlobalStore } from '@/stores';
import { configSettings } from '../config';
import { queryClient } from '@/queries';
import { browserTracingIntegration, init } from '@sentry/react';
import { QUERY_KEYS } from '../constants';

export const mainApi = axios.create({
    baseURL: configSettings.apiUrl,
});
export const authApi = axios.create({
    baseURL: configSettings.authApiUrl,
});
export const chatApi = axios.create({
    baseURL: configSettings.apiUrl,
});

// Request interceptor for mainApi
mainApi.interceptors.request.use(request => {
    const token = useAuthStore.getState().token;
    const UUID = useGlobalStore.getState().UUID;
    const sessionUUID = useGlobalStore.getState().sessionUUID;
    const getDeviceToken = useGlobalStore.getState().getDeviceToken;
    if (token) {
        request.headers['Authorization'] = `Bearer ${token}`;
    }
    request.headers['x-request-id'] = UUID;
    request.headers['x-session-uuid'] = sessionUUID;
    request.headers['x-session-id'] = getDeviceToken();
    return request;
});

// Request interceptor for chatApi
chatApi.interceptors.request.use(request => {
    const token = useAuthStore.getState().token;
    const UUID = useGlobalStore.getState().UUID;
    const sessionUUID = useGlobalStore.getState().sessionUUID;
    const getDeviceToken = useGlobalStore.getState().getDeviceToken;
    if (token) {
        request.headers['Authorization'] = `Bearer ${token}`;
    }
    request.headers['x-request-id'] = UUID;
    request.headers['x-session-uuid'] = sessionUUID;
    request.headers['x-session-id'] = getDeviceToken();
    return request;
});

// Response interceptor for mainApi
mainApi.interceptors.response.use(
    response => {
        const isLoginRoute = response.config?.url?.endsWith('/login');
        const ipAddress = response?.data?.ip;
        if (typeof response.data === 'object' && ipAddress) {
            useGlobalStore.setState({ ipAddress });
        }

        // tag devices that hit our auth endpoints
        const deviceToken = response.data?.deviceToken;
        if (isLoginRoute && deviceToken) {
            useGlobalStore.setState({ deviceToken });
        }
        return response;
    },
    error => {
        const status = error?.response?.status; // Possibility of response being undefined.
        const isAnyLoginRoute = error.response?.config?.url?.endsWith('/login');
        // TODO: Gtag
        // $gtag.event('exception', { description: error, fatal: false });

        // We do not force logout users due to a failed login attempt
        const isLoginRoute = error.config?.url?.endsWith('users/login');

        // Even on error, we want to tag the device
        const deviceToken = error.response?.data?.deviceToken;
        if (isAnyLoginRoute && deviceToken) {
            useGlobalStore.setState({ deviceToken });
        }

        if (!isLoginRoute && status === 401 && !queryClient.getQueryData([QUERY_KEYS.USER])) {
            useAuthStore.getState().logout();
        }

        return Promise.reject(error);
    }
);

// Response interceptor for chatApi
chatApi.interceptors.response.use(
    response => {
        const ipAddress = response?.data?.ip;
        if (typeof response.data === 'object' && ipAddress) {
            useGlobalStore.setState({ ipAddress });
        }
        return response;
    },
    error => {
        const status = error?.response?.status;

        if (status === 401 && !queryClient.getQueryData([QUERY_KEYS.USER])) {
            useAuthStore.getState().invalidateToken();
        }

        return Promise.reject(error);
    }
);

if (['staging', 'production'].includes(configSettings.environment)) {
    init({
        dsn: configSettings.sentryDsn,
        environment: configSettings.environment,
        // release: configSettings.releaseVersion, // TODO
        tracesSampleRate: 0, // or adjust sampling if you want performance tracing
        integrations: [browserTracingIntegration()],
        beforeSend(event, hint) {
            let ignoreEvent = false;

            const exValues = event.exception?.values || [];

            for (const ex of exValues) {
                const value = ex.value || '';
                const type = ex.type || '';

                if (value.includes('ResizeObserver')) ignoreEvent = true;
                else if (type === 'NavigationDuplicated') ignoreEvent = true;
                else if (type === 'Event') {
                    console.warn('Ignoring Event', type, value);
                    ignoreEvent = true;
                } else if (type === 'TypeError') {
                    if (
                        value.includes('naturalWidth') ||
                        value === "Cannot read properties of null (reading 'classList')" ||
                        value.includes("null is not an object (evaluating 't.target.parentNode.classList'")
                    ) {
                        console.warn('Ignoring Error', type, value);
                        ignoreEvent = true;
                    }
                } else if (type === 'SyntaxError' && value.includes('Unexpected token')) {
                    ignoreEvent = true;
                } else if (type === 'Error') {
                    const is4xx = /\s4[0-9]{2}$/.test(value);
                    const isRequestFailure = value.toLowerCase().includes('request failed with status code');

                    if (
                        value === 'Network Error' ||
                        (isRequestFailure && is4xx) ||
                        value === 'undefined' ||
                        value.includes('Navigation cancelled from') ||
                        value.includes('Redirected when going from')
                    ) {
                        console.warn('Ignoring Error', type, value);
                        ignoreEvent = true;
                    }
                } else if (hint?.originalException === 'Timeout') {
                    ignoreEvent = true;
                }

                if (ignoreEvent) break;
            }

            return ignoreEvent ? null : event;
        },
    });
}
